import { Injectable } from "@angular/core";
import { IProductResponse } from "@bca-compose/models/product-response";
import * as composeActions from "@bca-compose/store/compose.actions";
import { ComposeActionTypes } from "@bca-compose/store/compose.actions";
import { PageScrollService } from "@bca-core/modules/page-scroll/services/page-scroll.service";
import { ApiService } from "@bca-core/services/api.service";
import * as receiptActions from "@bca-receipt/store/receipt.actions";
import {
  MessageType,
  ProductIdentifier,
  ScrollPositionId,
  TravelerClass,
} from "@bca-shared/models/_enums";
import { IAddon } from "@bca-shared/models/addon";
import { IProduct } from "@bca-shared/models/product";
import { icons } from "@bca-shared/shared.constants";
import * as appActions from "@bca-store/app.actions";
import { IAction } from "@bca-store/models/action";
import { createAction, createActions } from "@bca-store/models/action-creator";
import { IRootState } from "@bca-store/store.reducers";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { select, Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { EMPTY, forkJoin, of, zip } from "rxjs";
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  tap,
  withLatestFrom,
} from "rxjs/operators";

@Injectable()
export class ComposeEffects {
  fetchRoute = createEffect(() =>
    this._actions.pipe(
      ofType(ComposeActionTypes.FetchRoute),
      switchMap(({ payload }: composeActions.FetchRoute) => {
        const { route, clazz } = payload;
        const { from, to } = route;
        this._store$.dispatch(
          this._createAction(
            new composeActions.RouteLoading({ loading: true }),
          ),
        );
        return zip(
          this._apiService.getRoutePrices(from, to, clazz).pipe(
            catchError((error) => {
              const message = {
                body: "NOTIFICATION.ERROR.ROUTE",
                type: MessageType.ERROR,
                context: { error },
                originalException: error,
              };
              this._store$.dispatch(
                this._createAction(new appActions.SetMessage(message)),
              );
              return EMPTY;
            }),
          ),
          of(route),
          of(clazz),
        );
      }),
      filter(([response]) => response.success),
      mergeMap(([{ addonProductItem }, route, clazz]) => {
        const configuredRoute = {
          ...route,
          price: { ...route.price, [clazz]: addonProductItem.price },
        };

        return this._createActions([
          new composeActions.RouteLoading({ loading: false }),
          clazz === TravelerClass.SECOND
            ? new composeActions.SetConfiguredRoute(configuredRoute)
            : new composeActions.ConfigureProduct(configuredRoute),
        ]);
      }),
    ),
  );

  fetchSecondClassPrice = createEffect(() =>
    this._actions.pipe(
      ofType(ComposeActionTypes.SetConfiguredRoute),
      filter(
        ({ payload: route }: composeActions.SetConfiguredRoute) =>
          route != null,
      ),
      filter(
        ({ payload: route }: composeActions.SetConfiguredRoute) =>
          route.from != null && route.to != null,
      ),
      filter(
        ({ payload: route }: composeActions.SetConfiguredRoute) =>
          route.from.id !== route.to.id,
      ),
      filter(
        ({ payload: route }: composeActions.SetConfiguredRoute) =>
          route.price == null || !route.price[TravelerClass.SECOND],
      ),
      map(({ payload: route }: composeActions.SetConfiguredRoute) => {
        return this._createAction(
          new composeActions.FetchRoute({
            route,
            clazz: TravelerClass.SECOND,
          }),
        );
      }),
    ),
  );

  fetchFirstClassPrice = createEffect(() =>
    this._actions.pipe(
      ofType(ComposeActionTypes.ConfigureProduct),
      filter(
        ({ payload: route }: composeActions.ConfigureProduct) => route != null,
      ),
      filter(
        ({ payload: route }: composeActions.ConfigureProduct) =>
          route.price === null ||
          (route.price[TravelerClass.FIRST] !== 0 &&
            !route.price[TravelerClass.FIRST]),
      ),
      map(({ payload: route }: composeActions.ConfigureProduct) => {
        return this._createAction(
          new composeActions.FetchRoute({
            route,
            clazz: TravelerClass.FIRST,
          }),
        );
      }),
    ),
  );

  fetchProducts = createEffect(() =>
    this._actions.pipe(
      ofType(ComposeActionTypes.FetchProducts),
      withLatestFrom(this._store$.pipe(select(({ compose }) => compose))),
      switchMap(() => {
        this._store$.dispatch(
          this._createAction(
            new composeActions.ProductsLoading({ loading: true }),
          ),
        );
        return this._apiService.getProducts().pipe(
          catchError((error) => {
            const message = {
              body: "NOTIFICATION.ERROR.PRODUCTS",
              type: MessageType.ERROR,
              context: { error },
              originalException: error,
            };
            this._store$.dispatch(
              this._createAction(new appActions.SetMessage(message)),
            );
            return EMPTY;
          }),
        );
      }),
      filter((response: IProductResponse) => response.success),
      switchMap(({ productItems }: IProductResponse) => {
        return forkJoin(
          productItems.map((product: IProduct) => {
            const { identifier } = product;
            return this._translate
              .get(`COMPOSE.PRODUCTS_OVERVIEW.PRODUCTS.${identifier}`)
              .pipe(
                map((translatedProduct: { ICON: string }) => {
                  const { ICON: icon } = translatedProduct;
                  return {
                    ...product,
                    icon,
                    hasSticker: identifier === ProductIdentifier.Dal,
                  };
                }),
              );
          }),
        );
      }),
      map((products: IProduct[]) => {
        return products.map((product) => {
          product.addons = product.addons.map((addon: IAddon) => {
            const { identifier } = addon;
            return {
              ...addon,
              needsPriceUpdate: identifier.includes(ProductIdentifier.Traject),
              clazz: identifier.includes("_UPDATE")
                ? TravelerClass.FIRST
                : TravelerClass.SECOND,
              selected: false,
            };
          });
          return product;
        });
      }),
      map((products: IProduct[]) => {
        return products.map((product) => {
          product.services = product.services.map((service: IAddon) => {
            const iconMapping = icons.find(
              (icon) => icon.identifier === service.identifier,
            );
            const url = iconMapping ? iconMapping.url : "";
            return {
              ...service,
              selected: false,
              url: url,
            };
          });
          return product;
        });
      }),
      mergeMap((products: IProduct[]) =>
        this._createActions([
          new composeActions.SetProducts(products),
          new composeActions.ProductsLoading({ loading: false }),
        ]),
      ),
    ),
  );

  deleteProductFromReceiptOnSelectProduct = createEffect(() =>
    this._actions.pipe(
      ofType(ComposeActionTypes.SelectProduct),
      filter((action: composeActions.SelectProduct) => action.payload != null),
      withLatestFrom(
        this._store$.pipe(select(({ receipt }) => receipt.products)),
      ),
      filter(([action, products]) => {
        const { product } = (action as composeActions.SelectProduct).payload;
        return products.some(({ id }) => id === product.id);
      }),
      map(([action]) => {
        const { product } = (action as composeActions.SelectProduct).payload;
        return this._createAction(new receiptActions.DeleteProduct(product));
      }),
    ),
  );

  scrollToExtrasOnSelectProduct = createEffect(
    () =>
      this._actions.pipe(
        ofType(ComposeActionTypes.SelectProduct),
        filter(
          (action: composeActions.SelectProduct) => action.payload != null,
        ),
        filter(
          ({ payload }: composeActions.SelectProduct) => payload.scrollToExtras,
        ),
        tap(() =>
          this._page.scrollTo(ScrollPositionId.PRODUCT_EXTRAS, {
            timeout: 375,
          }),
        ),
      ),
    { dispatch: false },
  );

  private _createAction(action: IAction): IAction {
    return createAction(action, ComposeEffects);
  }

  private _createActions(actions: IAction[]): IAction[] {
    return createActions(actions, ComposeEffects);
  }

  constructor(
    private _actions: Actions,
    private _translate: TranslateService,
    private _store$: Store<IRootState>,
    private _apiService: ApiService,
    private _page: PageScrollService,
  ) {}
}
